import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { environment } from '../environments/environment';
import { UiIconComponent } from './ui-icon.component';

interface ConnectedUser {
  id?: number; filiale_id?: number; nom?: string; prenom?: string; role?: string; type_user_libelle?: string; type_user_codification?: string;
  prestataire_libelle?: string; filiale_libelle?: string; image_url?: string; access_token?: string;
}
interface InfosModifiables {
  numero_cnam?: string; nature_piece_id?: number; nature_piece_libelle?: string; numero_piece?: string; validite_piece?: string;
  email?: string; email2?: string; telephone?: string; telephone2?: string;
  groupe_sanguin_id?: number; groupe_sanguin_libelle?: string; taille?: string; poids?: string;
}
interface PortalHome {
  profil: { id: number; matricule: string; nom: string; prenom: string; photo?: string; statut?: string; date_naissance?: string; groupe_sanguin?: string; actif: boolean; est_principal: boolean; infos_modifiables: InfosModifiables };
  couverture: { filiale?: string; police?: string; police_numero?: string; college?: string; garant?: string; souscripteur?: string; date_effet?: string; date_fin?: string };
  famille: Array<{ id: number; matricule: string; nom_complet: string; statut?: string; photo?: string; principal: boolean; actif: boolean; infos_modifiables: InfosModifiables }>;
  reseau: { reseau?: { libelle: string }; origine?: string; centres: Array<{ id: number; libelle: string; type_prestataire?: string; commune?: string; telephone?: string }> };
}
interface DutyPharmacy { id: number; libelle?: string; adresse?: string; telephone?: string; ville?: string; commune?: string; latitude?: string; longitude?: string; date_debut?: string; date_fin?: string; }
interface InsuredPrescription { id: number; numero?: string; date?: string; beneficiaire?: string; matricule?: string; acte?: string; garantie?: string; prestataire_id?: number; prestataire?: string; praticien?: string; quantite?: number; accordee: boolean; traitee: boolean; exclue: boolean; entente_prealable: boolean; document?: string; }
interface InsuredPrestation { id: number; numero?: string; date?: string; beneficiaire?: string; matricule?: string; acte?: string; garantie?: string; prestataire_id?: number; prestataire?: string; praticien?: string; montant?: number; part_assurance?: number; part_patient?: number; statut?: string; facturee: boolean; document?: string; }
interface InsuredExpense { id: number; numero_bon?: string; numero_bon_principal?: string; code?: string; codification?: string; created_at?: string; beneficiaire_nom?: string; beneficiaire_prenom?: string; matricule?: string; acte_libelle?: string; medicament_libelle?: string; garantie_libelle?: string; police_libelle?: string; garant_libelle?: string; prestataire_id?: number; prestataire_libelle?: string; quantite?: number; taux_couverture?: number; montant?: number; montant_accorde?: number; montant_remboursable?: number; montant_rejete?: number; part_assurance?: number; part_patient?: number; statut?: string; observation?: string; is_factured?: number; ged_file_url?: string; }
interface ExpenseService { id: number; created_at?: string; numero_bon?: string; acte_libelle?: string; medicament_libelle?: string; garantie_libelle?: string; quantite?: number; montant?: number; part_assurance?: number; part_patient?: number; montant_accorde?: number; montant_rejete?: number; statut?: string; statut_precedent?: string; dernier_statut?: string; observation?: string; ged_file_url?: string; }
interface ManagerHome {
  indicateurs: { prime_emise: number; nouveaux_contrats: number; montant_nouveaux_contrats: number; contrats_brouillon: number; polices_actives: number; beneficiaires_actifs: number };
  evolution: { labels: string[]; primes: number[]; contrats: number[] };
  hospitalisations: { labels: string[]; hospitalisations: number[]; visites: number[]; total_hospitalisations: number; total_visites: number };
  contrats_recents: Array<{ id: number; libelle?: string; numero?: string; garant?: string; souscripteur?: string; date_creation?: string; actif: boolean }>;
}
interface ProviderMetric { section: string; title: string; count: number; }
interface ProviderHome {
  centre: { id: number; libelle: string; type?: string; statut?: string; ville?: string; commune?: string; telephone?: string; actif: boolean };
  activite: ProviderMetric[];
  finance: ProviderMetric[];
  circuit_facture: ProviderMetric[];
  repartition_garantie: Array<{ title: string; nbr_actes: number; part_assurance: number }>;
  prestations_recentes: Array<{ id: number; matricule?: string; beneficiaire?: string; acte?: string; garantie?: string; part_assurance?: number; date?: string }>;
}
interface GuarantorHome {
  garant: { id: number; libelle: string; type?: string; statut?: string; email?: string; adresse?: string };
  indicateurs: { polices: number; beneficiaires: number; familles: number; factures_mois: number; montant_reclame: number; montant_accorde: number; factures_en_attente: number };
  evolution: { labels: string[]; montants: number[]; factures: number[] };
  polices_recentes: Array<{ id: number; libelle?: string; numero?: string; souscripteur?: string; beneficiaires: number; date_echeance?: string }>;
}
interface ApiResponse<T> { hasError: boolean; status?: { message?: string }; item?: T; }

type SheetName = 'card' | 'refund' | 'lost-card' | 'profile' | 'verify-insured' | 'edit-infos';
type ScreenName = 'network' | 'documents' | 'agreements' | 'exclusions' | 'drugs' | 'notifications';
const EDITABLE_FIELDS: Array<keyof InfosModifiables> = ['numero_cnam', 'nature_piece_id', 'numero_piece', 'validite_piece', 'email', 'email2', 'telephone', 'telephone2', 'groupe_sanguin_id', 'taille', 'poids'];
interface AppNotification { id: number; title: string; body: string; date: string; read: boolean; }
interface AppDocument { id: number; label: string; date: string; type: string; }

const DUMMY_PRIOR_AGREEMENTS = [
  "L'endoscopie (fibroscopie, coloscopie, anuscopie)", "L'électrodiagnostic (EMG, EEG), scanner, IRM, scintigraphie",
  "La chimiothérapie et la radiothérapie", "La kinésithérapie, les massages, la rééducation fonctionnelle",
  "L'appareillage et les prothèses (auditives, orthopédiques, dentaires)", "Le transport médicalisé programmé",
  "L'hospitalisation programmée hors urgence", "Les cures thermales et séjours de convalescence",
];
const DUMMY_GENERAL_EXCLUSIONS = [
  "La chirurgie esthétique et les actes de confort", "Les accessoires à usage médical non prescrits",
  "Les produits diététiques, vitamines et compléments alimentaires", "Les cures thermales non prescrites",
  "Les frais liés à l'exploration et au traitement de la stérilité", "Les accidents résultant d'une activité sportive professionnelle",
  "Les soins réalisés hors du réseau agréé sans entente préalable",
];
const DUMMY_EXCLUDED_DRUGS = [
  'ABC VITAMINE INJ B/1', 'ANTIDOULEUR GEL 100G', 'COMPLEMENT ALIMENTAIRE CP B/30', 'CREME HYDRATANTE 200ML',
  'FORTIFIANT SIROP FL/200ML', 'OREXIGENE CP B/20', 'PRODUIT AMAIGRISSANT CP B/60', 'TONIQUE CAPILLAIRE FL/100ML',
];
const DUMMY_NOTIFICATIONS: AppNotification[] = [
  { id: 1, title: 'Remboursement traité', body: 'Votre demande de remboursement a été validée.', date: new Date().toISOString(), read: false },
  { id: 2, title: 'Entente préalable requise', body: "Un acte prescrit nécessite une entente préalable.", date: new Date(Date.now() - 86400000).toISOString(), read: false },
  { id: 3, title: 'Nouvelle facture disponible', body: 'Une facture de votre prestataire est en ligne.', date: new Date(Date.now() - 3 * 86400000).toISOString(), read: true },
];
const DUMMY_DOCUMENTS: AppDocument[] = [
  { id: 1, label: "Attestation d'affiliation 2026", date: '2026-01-05', type: 'PDF' },
  { id: 2, label: 'Certificat de prise en charge', date: '2025-11-20', type: 'PDF' },
  { id: 3, label: 'Conditions générales du contrat', date: '2025-09-02', type: 'PDF' },
];
const ACTE_TYPES = ['Consultation', 'Imagerie & examens', 'Biologie', 'Optique', 'Dentaire', 'Hospitalisation', 'Transport médicalisé', 'Pharmacie'];

@Component({
  selector: 'app-accueil', standalone: true, imports: [UiIconComponent, DatePipe, FormsModule],
  templateUrl: './accueil.component.html', styleUrl: './accueil.component.scss'
})
export class AccueilComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  protected readonly user: ConnectedUser = JSON.parse(localStorage.getItem('medicareleeUser') || '{}');
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  protected readonly home = signal<PortalHome | null>(null);
  protected readonly insuredSection = signal<'overview' | 'pharmacies' | 'prescriptions' | 'prestations' | 'depenses'>('overview');
  protected readonly insuredItemsLoading = signal(false);
  protected readonly insuredItemsError = signal('');
  protected readonly pharmacies = signal<DutyPharmacy[]>([]);
  protected readonly prescriptions = signal<InsuredPrescription[]>([]);
  protected readonly prestations = signal<InsuredPrestation[]>([]);
  protected readonly depenses = signal<InsuredExpense[]>([]);
  protected readonly selectedExpense = signal<InsuredExpense | null>(null);
  protected readonly expenseServices = signal<ExpenseService[]>([]);
  protected readonly expenseServicesLoading = signal(false);
  protected readonly expenseServicesError = signal('');
  protected prestationDateFrom = '';
  protected prestationDateTo = '';
  protected prestationPrestataire = '';
  protected prescriptionDateFrom = '';
  protected prescriptionDateTo = '';
  protected prescriptionStatus = '';
  protected prescriptionPrestataire = '';
  protected depenseDateFrom = '';
  protected depenseDateTo = '';
  protected depensePrestataire = '';
  protected readonly managerHome = signal<ManagerHome | null>(null);
  protected readonly providerHome = signal<ProviderHome | null>(null);
  protected readonly guarantorHome = signal<GuarantorHome | null>(null);
  protected readonly isBeneficiaire = this.user.type_user_codification === 'USER_BENEF';
  protected readonly isGestionnaire = this.user.type_user_codification === 'USER_GESTIONNAIRE';
  protected readonly isPrestataire = this.user.type_user_codification === 'USER_PRESTATAIRE';
  protected readonly isGarant = this.user.type_user_codification === 'USER_GARANT';
  protected readonly isSouscripteur = this.user.type_user_codification === 'USER_SOUSCRIPTEUR';

  protected readonly activeSheet = signal<SheetName | null>(null);
  protected readonly activeScreen = signal<ScreenName | null>(null);
  protected readonly notifications = signal<AppNotification[]>(DUMMY_NOTIFICATIONS);
  protected readonly documents = signal<AppDocument[]>(DUMMY_DOCUMENTS);
  protected readonly priorAgreements = DUMMY_PRIOR_AGREEMENTS;
  protected readonly generalExclusions = DUMMY_GENERAL_EXCLUSIONS;
  protected readonly excludedDrugs = DUMMY_EXCLUDED_DRUGS;
  protected readonly acteTypes = ACTE_TYPES;
  protected networkSearch = '';
  protected agreementsSearch = '';
  protected exclusionsSearch = '';
  protected drugsSearch = '';
  // Demande de remboursement direct (RD) — aligné sur modal-saisie-rd côté gestionnaire
  protected rdDatePrestation = new Date().toISOString().slice(0, 10);
  protected rdBeneficiaireId: number | null = null;
  protected rdActeSearch = '';
  protected readonly rdActeResults = signal<any[]>([]);
  protected rdSelectedActe: any = null;
  protected rdPrestataireMode: 'existant' | 'prospect' = 'existant';
  protected rdPrestataireSearch = '';
  protected readonly rdPrestataireResults = signal<any[]>([]);
  protected rdSelectedPrestataire: any = null;
  protected rdPrestataireSearchDone = false;
  protected rdProspectLibelle = '';
  protected rdProspectAdresse = '';
  protected rdProspectTelephone = '';
  protected rdProspectId: number | null = null;
  protected rdMontantSaisi: number | null = null;
  protected rdPraticienSearch = '';
  protected readonly rdPraticienResults = signal<any[]>([]);
  protected rdSelectedPraticien: any = null;
  protected rdPraticienNouveauNom = '';
  protected rdPraticienNouveauPrenom = '';
  protected rdPraticienNouveauSpecialiteId: number | null = null;
  protected readonly rdSpecialites = signal<Array<{ id: number; libelle: string }>>([]);
  protected rdOrdonnanceImage: { fileBase64: unknown; fileName: unknown; extension: unknown; typeDocument: string } | null = null;
  protected rdOrdonnanceImageName = '';
  protected readonly rdCalcul = signal<any>(null);
  protected readonly rdCalculError = signal('');
  protected readonly rdLoadingCalcul = signal(false);
  protected readonly rdLoadingSave = signal(false);
  protected readonly rdSubmitted = signal(false);
  protected lostCardBeneficiaireId: number | null = null;
  protected lostCardDate = '';
  protected lostCardDocName = '';
  protected lostCardPhotoName = '';
  protected readonly lostCardSubmitted = signal(false);
  protected verifyMatricule = '';
  protected readonly verifyResult = signal<{ found: boolean; nom?: string; statut?: string; garantie?: string } | null>(null);
  protected readonly cardIndex = signal(0);
  private cardTouchStartX = 0;
  protected readonly naturesPiece = signal<Array<{ id: number; libelle: string }>>([]);
  protected readonly groupesSanguins = signal<Array<{ id: number; libelle: string }>>([]);
  protected editBeneficiaireId: number | null = null;
  protected editForm: InfosModifiables = {};
  protected readonly editSubmitted = signal(false);
  protected readonly editSubmitting = signal(false);
  protected readonly editError = signal('');

  ngOnInit(): void { if (this.isBeneficiaire || this.isGestionnaire || this.isPrestataire || this.isGarant || this.isSouscripteur) this.loadHome(); }

  protected openSheet(sheet: SheetName): void {
    this.activeSheet.set(sheet);
    if (sheet === 'card') this.cardIndex.set(0);
  }
  protected openScreen(screen: ScreenName): void {
    this.activeScreen.set(screen);
  }
  protected closeScreen(): void {
    this.activeScreen.set(null);
  }
  protected closeSheet(): void {
    this.activeSheet.set(null);
    this.rdSubmitted.set(false);
    this.lostCardSubmitted.set(false);
    this.verifyResult.set(null);
    this.editSubmitted.set(false);
    this.editError.set('');
  }

  protected editableMembers(): Array<{ id: number; matricule: string; label: string; infos: InfosModifiables }> {
    const data = this.home();
    if (!data) return [];
    if (data.famille.length) {
      return data.famille.map(member => ({ id: member.id, matricule: member.matricule, label: member.nom_complet, infos: member.infos_modifiables }));
    }
    return [{ id: data.profil.id, matricule: data.profil.matricule, label: `${data.profil.prenom} ${data.profil.nom}`, infos: data.profil.infos_modifiables }];
  }

  protected openEditInfos(): void {
    this.openSheet('edit-infos');
    this.editError.set('');
    this.editSubmitted.set(false);
    const members = this.editableMembers();
    const profileId = this.home()?.profil.id ?? null;
    const initial = members.find(member => member.id === profileId) || members[0];
    this.selectEditMember(initial ? initial.id : null);
    if (!this.naturesPiece().length) this.loadNaturesPiece();
    if (!this.groupesSanguins().length) this.loadGroupesSanguins();
  }

  protected selectEditMember(id: number | null): void {
    this.editBeneficiaireId = id;
    const member = this.editableMembers().find(candidate => candidate.id === id);
    this.editForm = member
      ? { ...member.infos, validite_piece: member.infos.validite_piece ? member.infos.validite_piece.slice(0, 10) : undefined }
      : {};
  }

  private loadNaturesPiece(): void {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    this.http.post<any>(`${environment.apiUrl}/naturePiece/getByCriteria`, { user_id: this.user.id, filiale_id: this.user.filiale_id, index: 0, size: 200, data: {} }, { headers })
      .subscribe({ next: res => this.naturesPiece.set(res?.items || []), error: () => {} });
  }

  private loadGroupesSanguins(): void {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    this.http.post<any>(`${environment.apiUrl}/groupeSanguin/getByCriteria`, { user_id: this.user.id, filiale_id: this.user.filiale_id, index: 0, size: 200, data: {} }, { headers })
      .subscribe({ next: res => this.groupesSanguins.set(res?.items || []), error: () => {} });
  }

  protected submitEditInfos(): void {
    if (!this.editBeneficiaireId || this.editSubmitting()) return;
    this.editError.set('');
    this.editSubmitting.set(true);
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    const payload: Record<string, unknown> = { beneficiaire_id: this.editBeneficiaireId };
    for (const field of EDITABLE_FIELDS) payload[field] = this.editForm[field] ?? null;
    this.http.post<any>(`${environment.apiUrl}/beneficiaire/espace/modifierInfos`, { data: payload }, { headers })
      .pipe(finalize(() => this.editSubmitting.set(false)))
      .subscribe({
        next: res => {
          if (res?.hasError) { this.editError.set(res.status?.message || 'Mise à jour impossible.'); return; }
          this.editSubmitted.set(true);
          this.loadHome();
        },
        error: error => this.editError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.')
      });
  }

  protected unreadNotifications(): number { return this.notifications().filter(n => !n.read).length; }
  protected markNotificationsRead(): void { this.notifications.update(items => items.map(n => ({ ...n, read: true }))); }

  protected cardEntries(): Array<{ id: number; matricule: string; label: string; photo?: string; dateNaissance?: string; statut?: string }> {
    const data = this.home();
    if (!data) return [];
    if (data.famille.length) {
      return data.famille.map(member => ({
        id: member.id, matricule: member.matricule, label: member.nom_complet, photo: member.photo, statut: member.statut,
        dateNaissance: member.id === data.profil.id ? data.profil.date_naissance : undefined,
      }));
    }
    return [{ id: data.profil.id, matricule: data.profil.matricule, label: `${data.profil.prenom} ${data.profil.nom}`, photo: data.profil.photo, dateNaissance: data.profil.date_naissance, statut: data.profil.statut }];
  }
  protected currentCard() { const entries = this.cardEntries(); return entries[this.cardIndex()] || null; }
  protected goToCard(index: number): void { this.cardIndex.set(index); }
  protected nextCard(): void { const total = this.cardEntries().length; if (total) this.cardIndex.set((this.cardIndex() + 1) % total); }
  protected prevCard(): void { const total = this.cardEntries().length; if (total) this.cardIndex.set((this.cardIndex() - 1 + total) % total); }
  protected onCardTouchStart(event: TouchEvent): void { this.cardTouchStartX = event.touches[0].clientX; }
  protected onCardTouchEnd(event: TouchEvent): void {
    const delta = event.changedTouches[0].clientX - this.cardTouchStartX;
    if (Math.abs(delta) > 40) delta < 0 ? this.nextCard() : this.prevCard();
  }

  protected filteredCentres(): PortalHome['reseau']['centres'] {
    const centres = this.home()?.reseau.centres || [];
    const search = this.networkSearch.trim().toLowerCase();
    if (!search) return centres;
    return centres.filter(c => [c.libelle, c.commune, c.type_prestataire].filter(Boolean).some(v => v!.toLowerCase().includes(search)));
  }

  private filterList(items: string[], search: string): string[] {
    const value = search.trim().toLowerCase();
    return value ? items.filter(item => item.toLowerCase().includes(value)) : items;
  }
  protected filteredAgreements(): string[] { return this.filterList(this.priorAgreements, this.agreementsSearch); }
  protected filteredExclusions(): string[] { return this.filterList(this.generalExclusions, this.exclusionsSearch); }
  protected filteredDrugs(): string[] { return this.filterList(this.excludedDrugs, this.drugsSearch); }

  protected onFilePicked(event: Event, target: 'lost-card-doc' | 'lost-card-photo'): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (target === 'lost-card-doc') this.lostCardDocName = file.name;
    else this.lostCardPhotoName = file.name;
  }

  // ---- Demande de remboursement direct (RD) ----
  private rdHeaders(): HttpHeaders { return new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` }); }
  private rdPost<T = any>(endpoint: string, data: Record<string, unknown>) {
    return this.http.post<T>(`${environment.apiUrl}/${endpoint}`, { user_id: this.user.id, filiale_id: this.user.filiale_id, ...data }, { headers: this.rdHeaders() });
  }

  protected openRdRequest(): void {
    this.openSheet('refund');
    this.resetRdForm();
    if (!this.rdSpecialites().length) this.rdLoadSpecialites();
  }

  private resetRdForm(): void {
    this.rdDatePrestation = new Date().toISOString().slice(0, 10);
    this.rdBeneficiaireId = null;
    this.rdActeSearch = ''; this.rdActeResults.set([]); this.rdSelectedActe = null;
    this.rdChangePrestataireMode('existant');
    this.rdMontantSaisi = null;
    this.rdOrdonnanceImage = null; this.rdOrdonnanceImageName = '';
    this.rdCalcul.set(null); this.rdCalculError.set(''); this.rdSubmitted.set(false);
  }

  private rdLoadSpecialites(): void {
    this.rdPost('specialite/getByCriteria', { index: 0, size: 1000, data: {} })
      .subscribe({ next: (res: any) => this.rdSpecialites.set(res?.items || []), error: () => {} });
  }

  protected rdSelectMember(id: number): void {
    this.rdBeneficiaireId = id;
    this.rdCalcul.set(null); this.rdCalculError.set('');
  }

  protected rdOnDateChange(): void {
    this.rdCalcul.set(null); this.rdCalculError.set('');
  }

  protected rdSearchActe(): void {
    if (!this.rdActeSearch) { this.rdActeResults.set([]); return; }
    this.rdPost('acte/getByCriteria', { index: 0, size: 20, search_key: this.rdActeSearch, data: {} })
      .subscribe({ next: (res: any) => this.rdActeResults.set(res?.items || []), error: () => this.rdActeResults.set([]) });
  }
  protected rdSelectActeItem(acte: any): void {
    this.rdSelectedActe = acte; this.rdActeSearch = acte.libelle; this.rdActeResults.set([]);
    this.rdCalcul.set(null); this.rdCalculError.set('');
  }

  protected rdChangePrestataireMode(mode: 'existant' | 'prospect'): void {
    this.rdPrestataireMode = mode;
    this.rdSelectedPrestataire = null;
    this.rdProspectLibelle = ''; this.rdProspectAdresse = ''; this.rdProspectTelephone = ''; this.rdProspectId = null;
    this.rdPrestataireSearch = ''; this.rdPrestataireResults.set([]); this.rdPrestataireSearchDone = false;
    this.rdCalcul.set(null); this.rdCalculError.set('');
    if (mode === 'prospect') this.rdClearPraticienSelection();
  }
  protected rdOnProspectFieldChange(): void {
    this.rdProspectId = null; this.rdCalcul.set(null); this.rdCalculError.set('');
  }
  protected rdSearchPrestataires(): void {
    this.rdSelectedPrestataire = null;
    if (!this.rdPrestataireSearch) { this.rdPrestataireResults.set([]); this.rdPrestataireSearchDone = false; return; }
    this.rdPost('prestataire/searchAllForConvention', { search_key: this.rdPrestataireSearch, index: 0, size: 20 })
      .subscribe({
        next: (res: any) => { this.rdPrestataireResults.set(res?.items && !res.hasError ? res.items : []); this.rdPrestataireSearchDone = true; },
        error: () => { this.rdPrestataireResults.set([]); this.rdPrestataireSearchDone = true; }
      });
  }
  protected rdSelectPrestataire(prestataire: any): void {
    this.rdSelectedPrestataire = prestataire; this.rdPrestataireResults.set([]); this.rdPrestataireSearch = prestataire.libelle;
    this.rdCalcul.set(null); this.rdCalculError.set('');
  }
  protected rdBasculerVersProspect(): void {
    this.rdPrestataireMode = 'prospect';
    this.rdProspectLibelle = this.rdPrestataireSearch; this.rdProspectId = null;
    this.rdSelectedPrestataire = null; this.rdPrestataireResults.set([]);
    this.rdCalcul.set(null); this.rdCalculError.set('');
    this.rdClearPraticienSelection();
  }

  protected rdSearchPraticiens(): void {
    if (!this.rdPraticienSearch) { this.rdPraticienResults.set([]); return; }
    this.rdPost('praticien/getByCriteria', { index: 0, size: 20, search_key: this.rdPraticienSearch, data: {} })
      .subscribe({ next: (res: any) => this.rdPraticienResults.set(res?.items && !res.hasError ? res.items : []), error: () => this.rdPraticienResults.set([]) });
  }
  protected rdSelectPraticien(praticien: any): void {
    this.rdSelectedPraticien = praticien; this.rdPraticienResults.set([]);
    this.rdPraticienSearch = `${praticien.nom} ${praticien.prenom || ''}`.trim();
    this.rdPraticienNouveauNom = ''; this.rdPraticienNouveauPrenom = '';
  }
  protected rdClearPraticienSelection(): void {
    this.rdSelectedPraticien = null; this.rdPraticienSearch = '';
    this.rdPraticienNouveauNom = ''; this.rdPraticienNouveauPrenom = ''; this.rdPraticienNouveauSpecialiteId = null;
  }

  protected rdUploadOrdonnance(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const fileName = file.name.split('.')[0];
    const parts = file.name.split('.');
    const extension = parts[parts.length - 1];
    this.rdOrdonnanceImageName = file.name;
    const reader = new FileReader();
    reader.onload = readerEvent => {
      const data = (readerEvent.target as any).result as string;
      const fileBase64 = data.split(',')[1];
      this.rdOrdonnanceImage = { fileBase64, fileName, extension, typeDocument: 'ordonnance' };
    };
    reader.readAsDataURL(file);
  }

  protected rdPeutCalculer(): boolean {
    if (!this.rdBeneficiaireId || !this.rdSelectedActe || !this.rdDatePrestation) return false;
    return this.rdPrestataireMode === 'existant' ? !!this.rdSelectedPrestataire : !!(this.rdProspectLibelle && this.rdMontantSaisi);
  }

  protected rdCalculer(): void {
    if (!this.rdPeutCalculer()) return;
    this.rdLoadingCalcul.set(true); this.rdCalcul.set(null); this.rdCalculError.set('');
    if (this.rdPrestataireMode === 'prospect' && !this.rdProspectId) {
      this.rdPost('prestataireProspect/create', { datas: [{ libelle: this.rdProspectLibelle, adresse: this.rdProspectAdresse, telephone: this.rdProspectTelephone }] })
        .subscribe({
          next: (res: any) => {
            if (res && !res.hasError) { this.rdProspectId = res.item.id; this.rdLancerCalcul(); }
            else { this.rdLoadingCalcul.set(false); this.rdCalculError.set(res?.status?.message || 'Erreur'); }
          },
          error: error => { this.rdLoadingCalcul.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
        });
    } else {
      this.rdLancerCalcul();
    }
  }

  private rdLancerCalcul(): void {
    const member = this.editableMembers().find(m => m.id === this.rdBeneficiaireId);
    const requestData: Record<string, unknown> = { acte_id: this.rdSelectedActe.id, matricule: member?.matricule };
    if (this.rdMontantSaisi) requestData['montant_saisi'] = this.rdMontantSaisi;
    if (this.rdPrestataireMode === 'prospect' && this.rdProspectId) requestData['prestataire_prospect_id'] = this.rdProspectId;
    const payload: Record<string, unknown> = { date_prestation: this.rdDatePrestation, data: requestData };
    if (this.rdPrestataireMode === 'existant' && this.rdSelectedPrestataire) payload['prestataire_id'] = this.rdSelectedPrestataire.id;

    this.rdPost('bareme/deepSeekRd', payload).subscribe({
      next: (res: any) => {
        this.rdLoadingCalcul.set(false);
        if (res && !res.hasError) {
          if (!res.item.is_remboursement_direct) { this.rdCalculError.set("Cet assuré n'est pas éligible au remboursement direct pour cet acte."); return; }
          this.rdCalcul.set(res.item);
        } else {
          this.rdCalculError.set(res?.status?.message || 'Erreur de calcul');
        }
      },
      error: error => { this.rdLoadingCalcul.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
    });
  }

  protected rdSave(): void {
    const calcul = this.rdCalcul();
    if (!calcul) return;
    this.rdLoadingSave.set(true);
    if (this.rdPrestataireMode === 'prospect') {
      if (this.rdProspectId) { this.rdResolvePraticienThenSave(this.rdProspectId, null); return; }
      this.rdPost('prestataireProspect/create', { datas: [{ libelle: this.rdProspectLibelle, adresse: this.rdProspectAdresse, telephone: this.rdProspectTelephone }] })
        .subscribe({
          next: (res: any) => {
            if (res && !res.hasError) { this.rdProspectId = res.item.id; this.rdResolvePraticienThenSave(this.rdProspectId, null); }
            else { this.rdLoadingSave.set(false); this.rdCalculError.set(res?.status?.message || 'Erreur'); }
          },
          error: error => { this.rdLoadingSave.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
        });
    } else {
      this.rdResolvePraticienThenSave(null, this.rdSelectedPrestataire.id);
    }
  }

  private rdResolvePraticienThenSave(prestataireProspectId: number | null, prestataireId: number | null): void {
    if (this.rdSelectedPraticien) { this.rdCreateOrdonnanceThenSave(this.rdSelectedPraticien.id, prestataireProspectId, prestataireId); return; }
    if (this.rdPraticienNouveauNom) {
      this.rdPost('praticien/create', { datas: [{ code: 'PRAT-RD-' + Date.now(), nom: this.rdPraticienNouveauNom, prenom: this.rdPraticienNouveauPrenom, specialite_id: this.rdPraticienNouveauSpecialiteId }] })
        .subscribe({
          next: (res: any) => {
            if (res && !res.hasError) this.rdCreateOrdonnanceThenSave(res.item.id, prestataireProspectId, prestataireId);
            else { this.rdLoadingSave.set(false); this.rdCalculError.set(res?.status?.message || 'Erreur'); }
          },
          error: error => { this.rdLoadingSave.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
        });
    } else {
      this.rdCreateOrdonnanceThenSave(null, prestataireProspectId, prestataireId);
    }
  }

  private rdCreateOrdonnanceThenSave(praticienId: number | null, prestataireProspectId: number | null, prestataireId: number | null): void {
    const member = this.editableMembers().find(m => m.id === this.rdBeneficiaireId);
    const calcul = this.rdCalcul();
    const ordonnancePayload: Record<string, unknown> = {
      code: 'ORD-RD-' + Date.now(),
      libelle: `Ordonnance RD ${member?.matricule || ''}`,
      filiale_id: this.user.filiale_id,
      beneficiaire_id: this.rdBeneficiaireId,
      garantie_id: calcul.garantie_id,
      acte_id: this.rdSelectedActe.id,
      praticien_id: praticienId,
    };
    if (prestataireId) ordonnancePayload['prestataire_id'] = prestataireId;
    if (this.rdOrdonnanceImage) ordonnancePayload['file_data'] = this.rdOrdonnanceImage;

    this.rdPost('ordonnance/create', { datas: [ordonnancePayload] }).subscribe({
      next: (res: any) => {
        if (res && !res.hasError) this.rdCreatePrescriptionActeRd(res.item.id, prestataireProspectId, prestataireId);
        else { this.rdLoadingSave.set(false); this.rdCalculError.set(res?.status?.message || 'Erreur'); }
      },
      error: error => { this.rdLoadingSave.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
    });
  }

  private rdCreatePrescriptionActeRd(ordonnanceId: number, prestataireProspectId: number | null, prestataireId: number | null): void {
    const calcul = this.rdCalcul();
    const payee = calcul.remboursement_direct_payee;
    const observation = payee ? `Remboursement direct à rembourser à : ${payee.label} (${payee.role})` : '';
    const fields: Record<string, unknown> = {
      filiale_id: this.user.filiale_id,
      beneficiaire_id: this.rdBeneficiaireId,
      acte_id: this.rdSelectedActe.id,
      garantie_id: calcul.garantie_id,
      bareme_id: calcul.bareme_id,
      ordonnance_id: ordonnanceId,
      prix_unitaire: calcul.montant,
      montant: calcul.montant,
      part_patient: calcul.part_patient,
      part_assurance: calcul.part_assurance,
      montant_accorde: calcul.part_assurance,
      montant_remboursable: calcul.part_assurance,
      is_accorde: 0,
      is_traite: 0,
      observation,
      origine_saisie: 'ASSURE',
    };
    if (prestataireId) fields['prestataire_id'] = prestataireId;
    if (prestataireProspectId) fields['prestataire_prospect_id'] = prestataireProspectId;

    this.rdPost('prescriptionActeRd/create', { datas: [fields] }).subscribe({
      next: (res: any) => {
        this.rdLoadingSave.set(false);
        if (res && !res.hasError) this.rdSubmitted.set(true);
        else this.rdCalculError.set(res?.status?.message || 'Erreur');
      },
      error: error => { this.rdLoadingSave.set(false); this.rdCalculError.set(error?.error?.status?.message || 'Le service est momentanément indisponible.'); }
    });
  }

  protected submitLostCard(): void {
    if (!this.lostCardBeneficiaireId || !this.lostCardDate) return;
    this.lostCardSubmitted.set(true);
  }
  protected resetLostCard(): void {
    this.lostCardBeneficiaireId = null; this.lostCardDate = ''; this.lostCardDocName = ''; this.lostCardPhotoName = '';
    this.lostCardSubmitted.set(false);
  }

  protected submitVerifyInsured(): void {
    if (!this.verifyMatricule.trim()) return;
    this.verifyResult.set({ found: true, nom: 'AHOUSSI Jean-Vincent', statut: 'Actif', garantie: 'Ambulatoire + Hospitalisation' });
  }

  protected loadHome(): void {
    this.loading.set(true); this.error.set('');
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    const endpoint = this.isBeneficiaire ? 'beneficiaire' : this.isGestionnaire ? 'gestionnaire' : this.isPrestataire ? 'prestataire' : this.isGarant ? 'garant' : 'souscripteur';
    this.http.get<ApiResponse<PortalHome | ManagerHome | ProviderHome | GuarantorHome>>(`${environment.apiUrl}/${endpoint}/espace/accueil`, { headers })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: response => {
          if (!response.item) { this.error.set(response.status?.message || 'Données indisponibles.'); return; }
          if (this.isBeneficiaire) this.home.set(response.item as PortalHome);
          else if (this.isGestionnaire) this.managerHome.set(response.item as ManagerHome);
          else if (this.isPrestataire) this.providerHome.set(response.item as ProviderHome);
          else this.guarantorHome.set(response.item as GuarantorHome);
        },
        error: error => this.error.set(error?.error?.status?.message || 'Impossible de charger votre espace.')
      });
  }

  protected openInsuredSection(section: 'pharmacies' | 'prescriptions' | 'prestations', force = false): void {
    this.insuredSection.set(section);
    this.insuredItemsError.set('');
    const target = section === 'pharmacies' ? this.pharmacies() : section === 'prescriptions' ? this.prescriptions() : this.prestations();
    if (target.length && !force) return;
    this.insuredItemsLoading.set(true);
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    const resource = section === 'pharmacies' ? 'pharmacies-garde' : section;
    this.http.get<ApiResponse<DutyPharmacy[] | InsuredPrescription[] | InsuredPrestation[]>>(`${environment.apiUrl}/beneficiaire/espace/${resource}`, { headers })
      .pipe(finalize(() => this.insuredItemsLoading.set(false)))
      .subscribe({
        next: response => {
          if (!response.item) { this.insuredItemsError.set(response.status?.message || 'Données indisponibles.'); return; }
          if (section === 'pharmacies') this.pharmacies.set(response.item as DutyPharmacy[]);
          else if (section === 'prescriptions') this.prescriptions.set(response.item as InsuredPrescription[]);
          else this.prestations.set(response.item as InsuredPrestation[]);
        },
        error: error => this.insuredItemsError.set(error?.error?.status?.message || 'Impossible de charger les informations.')
      });
  }

  protected showInsuredOverview(): void { this.insuredSection.set('overview'); }

  protected openExpenses(force = false): void {
    this.insuredSection.set('depenses');
    this.insuredItemsError.set('');
    if (this.depenses().length && !force) return;
    const profile = this.home()?.profil;
    if (!profile) return;
    this.insuredItemsLoading.set(true);
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    const data: Record<string, unknown> = { filiale_id: JSON.parse(localStorage.getItem('medicareleeUser') || '{}').filiale_id };
    if (!profile.est_principal) data['beneficiaire_id'] = profile.id;
    this.http.post<any>(`${environment.apiUrl}/sinistre/getByCriteria`, {
      data, matricule_assure: profile.est_principal ? profile.matricule : null,
      index: 0, size: 200, order_by: '-created_at'
    }, { headers }).pipe(finalize(() => this.insuredItemsLoading.set(false))).subscribe({
      next: response => {
        if (response?.hasError) { this.insuredItemsError.set(response.status?.message || 'Dépenses indisponibles.'); return; }
        this.depenses.set(response?.items || []);
      },
      error: error => this.insuredItemsError.set(error?.error?.status?.message || 'Impossible de charger les dépenses.')
    });
  }

  protected reloadInsuredSection(): void {
    const section = this.insuredSection();
    if (section === 'depenses') this.openExpenses(true);
    else if (section !== 'overview') this.openInsuredSection(section, true);
  }

  protected prestationProviders(): Array<{ id: string; label: string }> {
    const values = new Map<string, string>();
    this.prestations().forEach(item => { if (item.prestataire_id && item.prestataire) values.set(String(item.prestataire_id), item.prestataire); });
    return [...values].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }

  private inDateRange(date: string | undefined, from: string, to: string): boolean {
    if (!date) return !from && !to;
    const value = date.slice(0, 10);
    return (!from || value >= from) && (!to || value <= to);
  }

  protected filteredPrestations(): InsuredPrestation[] {
    return this.prestations().filter(item =>
      this.inDateRange(item.date, this.prestationDateFrom, this.prestationDateTo) &&
      (!this.prestationPrestataire || String(item.prestataire_id) === this.prestationPrestataire)
    );
  }

  protected prescriptionStatusOf(item: InsuredPrescription): string {
    if (item.exclue) return 'EXCLUE';
    if (item.entente_prealable) return 'ENTENTE_PREALABLE';
    return 'SANS_ENTENTE';
  }

  protected prescriptionProviders(): Array<{ id: string; label: string }> {
    const values = new Map<string, string>();
    this.prescriptions().forEach(item => { if (item.prestataire_id && item.prestataire) values.set(String(item.prestataire_id), item.prestataire); });
    return [...values].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }

  protected filteredPrescriptions(): InsuredPrescription[] {
    return this.prescriptions().filter(item =>
      this.inDateRange(item.date, this.prescriptionDateFrom, this.prescriptionDateTo) &&
      (!this.prescriptionStatus || this.prescriptionStatusOf(item) === this.prescriptionStatus) &&
      (!this.prescriptionPrestataire || String(item.prestataire_id) === this.prescriptionPrestataire)
    );
  }

  protected expenseProviders(): Array<{ id: string; label: string }> {
    const values = new Map<string, string>();
    this.depenses().forEach(item => { if (item.prestataire_id && item.prestataire_libelle) values.set(String(item.prestataire_id), item.prestataire_libelle); });
    return [...values].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }

  protected filteredExpenses(): InsuredExpense[] {
    return this.depenses().filter(item =>
      this.inDateRange(item.created_at, this.depenseDateFrom, this.depenseDateTo) &&
      (!this.depensePrestataire || String(item.prestataire_id) === this.depensePrestataire)
    );
  }

  protected expenseTotal(field: 'montant' | 'part_assurance' | 'part_patient'): number {
    return this.filteredExpenses().reduce((total, item) => total + Number(item[field] || 0), 0);
  }

  protected showExpenseDetail(expense: InsuredExpense): void {
    this.selectedExpense.set(expense);
    this.expenseServices.set([]);
    this.expenseServicesError.set('');
    this.expenseServicesLoading.set(true);
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.user.access_token || ''}` });
    const filialeId = JSON.parse(localStorage.getItem('medicareleeUser') || '{}').filiale_id;
    this.http.post<any>(`${environment.apiUrl}/prestationActe/getByCriteria`, {
      data: { filiale_id: filialeId, sinistre_id: expense.id }, index: 0, size: 200, order_by: '-created_at'
    }, { headers }).pipe(finalize(() => this.expenseServicesLoading.set(false))).subscribe({
      next: response => {
        if (response?.hasError) { this.expenseServicesError.set(response.status?.message || 'Détails indisponibles.'); return; }
        this.expenseServices.set(response?.items || []);
      },
      error: error => this.expenseServicesError.set(error?.error?.status?.message || 'Impossible de charger les prestations associées.')
    });
  }
  protected closeExpenseDetail(): void { this.selectedExpense.set(null); this.expenseServices.set([]); }

  protected statusLabel(status?: string): string {
    if (!status) return 'En cours';
    const labels: Record<string, string> = {
      ATTENTE_PREVALIDATION_PRESTATAIRE: 'En attente de prévalidation', VALIDE_PRESTATAIRE: 'Validée par le prestataire',
      VALIDE_GESTIONNAIRE: 'Validée par le gestionnaire', REJETE_GESTIONNAIRE: 'Rejetée par le gestionnaire',
      ANNULER_PRESTATAIRE: 'Annulée par le prestataire', EN_DECOMPTE_SINISTRE: 'En décompte',
      DECOMPTE_SINISTRE: 'Décomptée', EN_COMPTA: 'En comptabilité', VALIDE_COMPTA: 'Validée en comptabilité', REGLE: 'Réglée'
    };
    return labels[status] || status.toLowerCase().replaceAll('_', ' ').replace(/^./, value => value.toUpperCase());
  }

  protected pharmacyMapsUrl(pharmacy: DutyPharmacy): string {
    if (pharmacy.latitude && pharmacy.longitude) return `https://www.google.com/maps?q=${encodeURIComponent(pharmacy.latitude)},${encodeURIComponent(pharmacy.longitude)}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([pharmacy.libelle, pharmacy.commune, pharmacy.ville].filter(Boolean).join(' '))}`;
  }

  protected initials(name?: string): string {
    return (name || '').split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  }

  protected amount(value?: number): string {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(value || 0));
  }

  protected chartHeight(value: number, values: number[]): number {
    const maximum = Math.max(...values, 1);
    return Math.max(4, Math.round((value / maximum) * 100));
  }

  protected metric(items: ProviderMetric[], section: string): number {
    return Number(items.find(item => item.section === section)?.count || 0);
  }

  protected logout(): void {
    localStorage.removeItem('medicareleeUser');
    void this.router.navigateByUrl('/');
    window.location.reload();
  }
}
